// src/pages/alerts/OwnerAlertTemplates.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Paper, Typography, Button, IconButton, Tooltip,
  TextField, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl, Snackbar, Alert, LinearProgress
} from '@mui/material';
import { Add, Edit, Refresh, Visibility, CheckCircle, Block } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';
import { todayDateInput } from '../../utils/dateUtils';

// T6: عبر services/api المركزي (يرفق التوكن وX-ROLE-ID) بدل axios العام.
const CHANNELS = ['InApp', 'Email', 'SMS', 'WhatsApp'];

const useSnack = () => {
  const [snack, setSnack] = useState({open:false,msg:'',sev:'success'});
  const show = useCallback((msg, sev='success') => setSnack({open:true,msg,sev}), []);
  const onClose = () => setSnack(s => ({...s, open:false}));
  return { snack, show, onClose };
};

const applyVars = (s, vars) =>
  String(s || '')
    .replace(/\{\{horse_name\}\}/g, vars.horse_name || '')
    .replace(/\{\{procedures\}\}/g, vars.procedures || '')
    .replace(/\{\{date\}\}/g, vars.date || '')
    .replace(/\{\{notes\}\}/g, vars.notes || '');

export default function OwnerAlertTemplates() {
  const { t, i18n } = useTranslation();
  const { snack, show, onClose } = useSnack();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isRtl = String(i18n.language || '').startsWith('ar');

  const sampleVars = useMemo(() => ({
    horse_name: t('ownerAlertTemplates.sampleHorse'),
    procedures: t('ownerAlertTemplates.sampleProcedures'),
    date: todayDateInput(),
    notes: t('ownerAlertTemplates.sampleNotes')
  }), [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/Alerts/templates`, { params: { q }});
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      show(t('ownerAlertTemplates.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [q, show, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({
      template_key: '',
      title_ar: '',
      body_ar: '',
      title_en: '',
      body_en: '',
      channels: ['InApp'],
      is_active: 1
    });
    setDlgOpen(true);
  };
  const openEdit = (row) => {
    setEditing({
      ...row,
      channels: String(row.channels_csv || 'InApp').split(',').filter(Boolean)
    });
    setDlgOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        template_key: editing.template_key,
        title_ar: editing.title_ar,
        body_ar: editing.body_ar,
        title_en: editing.title_en,
        body_en: editing.body_en,
        channels: editing.channels,
        is_active: editing.is_active ? 1 : 0
      };
      if (editing.template_id) {
        await api.put(`/Alerts/templates/${editing.template_id}`, payload);
        show(t('ownerAlertTemplates.updateSuccess'));
      } else {
        await api.post(`/Alerts/templates`, payload);
        show(t('ownerAlertTemplates.createSuccess'));
      }
      setDlgOpen(false);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || t('ownerAlertTemplates.saveError');
      show(msg, 'error');
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/Alerts/templates/${row.template_id}/toggle`);
      show(row.is_active ? t('ownerAlertTemplates.deactivateSuccess') : t('ownerAlertTemplates.activateSuccess'));
      await load();
    } catch {
      show(t('ownerAlertTemplates.toggleError'), 'error');
    }
  };
  const {
    page,
    rowsPerPage,
    paginatedItems,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(rows, [q]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('ownerAlertTemplates.title', 'Owner message templates')}
        subtitle={t('ownerAlertTemplates.pageHint', 'Manage owner alert templates and the delivery channels used for horse updates.')}
        count={rows.length}
        actions={(
          <>
          <Tooltip title={t('refresh', 'Refresh')}>
            <span>
              <IconButton onClick={load} disabled={loading}><Refresh/></IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<Add/>} onClick={openCreate} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('ownerAlertTemplates.newTemplate', 'New template')}
          </Button>
          </>
        )}
      />

      <Paper elevation={0} sx={{ ...cardSx, p:2, mb:2 }}>
        <TextField
          label={t('search', 'Search')}
          size="small"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={(e)=>{ if (e.key==='Enter') load(); }}
          sx={{ width: 380, maxWidth: '100%' }}
        />
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <TableContainer component={Paper} sx={{ ...cardSx, ...smartTableContainerSx, overflow: 'hidden' }}>
        <Table size="small" sx={smartTableSx}>
          <TableHead sx={smartTableHeadSx}>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>{t('key', 'Key')}</TableCell>
              <TableCell>{t('channels', 'Channels')}</TableCell>
              <TableCell>{t('title_ar', 'Title (AR)')}</TableCell>
              <TableCell>{t('title_en', 'Title (EN)')}</TableCell>
              <TableCell>{t('status', 'Status')}</TableCell>
              <TableCell align="center" sx={{ minWidth: 170 }}>{t('actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map((r, i) => (
              <TableRow key={r.template_id}>
                <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                <TableCell>{r.template_key}</TableCell>
                <TableCell>
                  {String(r.channels_csv || '').split(',').filter(Boolean).map((c) => (
                    <Chip key={c} size="small" label={c} sx={{ mr: .5 }}/>
                  ))}
                </TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.title_ar || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 220, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{r.title_en || '—'}</TableCell>
                <TableCell>
                  {r.is_active ? <Chip size="small" color="success" label={t('active', 'Active')}/> : <Chip size="small" color="default" label={t('inactive', 'Inactive')}/>}
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                  <Tooltip title={t('preview', 'Preview')}>
                    <span>
                      <IconButton onClick={() => openEdit(r)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}><Visibility/></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('edit', 'Edit')}>
                    <span>
                      <IconButton onClick={() => openEdit(r)} sx={smartActionButtonSx(COLORS.gold, COLORS.goldSoft)}><Edit/></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={r.is_active ? t('deactivate', 'Deactivate') : t('activate', 'Activate')}>
                    <span>
                      <IconButton onClick={() => toggleActive(r)} sx={smartActionButtonSx(r.is_active ? COLORS.rose : COLORS.green, r.is_active ? COLORS.roseSoft : COLORS.greenSoft)}>
                        {r.is_active ? <Block/> : <CheckCircle/>}
                      </IconButton>
                    </span>
                  </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading && (
              <TableRow><TableCell colSpan={7} align="center">{q ? t('no_search_results') : t('no_data', 'No data')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <SmartTablePagination count={rows.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </TableContainer>

      {/* Dialog: create/edit */}
      <Dialog open={dlgOpen} onClose={()=>setDlgOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing?.template_id ? t('ownerAlertTemplates.editTemplate') : t('ownerAlertTemplates.newTemplate')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt:1 }}>
            <TextField
              label={t('ownerAlertTemplates.templateKey')}
              value={editing?.template_key || ''}
              onChange={(e)=>setEditing(s=>({...s, template_key: e.target.value}))}
              disabled={!!editing?.template_id}
              helperText={t('ownerAlertTemplates.templateKeyHint')}
              required
            />

            <FormControl>
              <InputLabel>{t('channels')}</InputLabel>
              <Select
                multiple
                label={t('channels')}
                value={editing?.channels || []}
                onChange={(e)=>setEditing(s=>({...s, channels: e.target.value}))}
                renderValue={(sel)=>sel.join(', ')}
              >
                {CHANNELS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="subtitle2">{t('arabic')}</Typography>
            <TextField
              label={t('ownerAlertTemplates.titleAr')}
              value={editing?.title_ar || ''}
              onChange={(e)=>setEditing(s=>({...s, title_ar: e.target.value}))}
            />
            <TextField
              label={t('ownerAlertTemplates.bodyAr')}
              value={editing?.body_ar || ''}
              onChange={(e)=>setEditing(s=>({...s, body_ar: e.target.value}))}
              multiline minRows={4}
            />

            <Typography variant="subtitle2">{t('english')}</Typography>
            <TextField
              label={t('ownerAlertTemplates.titleEn')}
              value={editing?.title_en || ''}
              onChange={(e)=>setEditing(s=>({...s, title_en: e.target.value}))}
            />
            <TextField
              label={t('ownerAlertTemplates.bodyEn')}
              value={editing?.body_en || ''}
              onChange={(e)=>setEditing(s=>({...s, body_en: e.target.value}))}
              multiline minRows={4}
            />

            <Typography variant="subtitle2">{t('ownerAlertTemplates.preview')}</Typography>
            <Paper variant="outlined" sx={{ p:1.5 }}>
              <Typography variant="overline">AR</Typography>
              <Typography variant="subtitle2">{applyVars(editing?.title_ar, sampleVars) || '-'}</Typography>
              <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>
                {applyVars(editing?.body_ar, sampleVars) || '-'}
              </Typography>
              <Box sx={{ height: 8 }}/>
              <Typography variant="overline">EN</Typography>
              <Typography variant="subtitle2">{applyVars(editing?.title_en, sampleVars) || '-'}</Typography>
              <Typography variant="body2" sx={{ whiteSpace:'pre-wrap' }}>
                {applyVars(editing?.body_en, sampleVars) || '-'}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDlgOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>{t('save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} onClose={onClose} autoHideDuration={2500}>
        <Alert severity={snack.sev} onClose={onClose}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
